import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './WeightCalculatorPopup.module.css';

const STORAGE_KEY = 'pof_weight_calculator_entries_v1';

const loadInitialEntries = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => {
        const label = typeof entry.label === 'string' ? entry.label.trim() : '';
        const weight = Number(entry.weight);
        if (!label || !Number.isFinite(weight) || weight <= 0) return null;
        return {
          id: entry.id || `saved-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          label,
          weight,
          source: entry.source === 'catalog' ? 'catalog' : 'custom',
          gearId: entry.gearId || null,
        };
      })
      .filter(Boolean);
  } catch (error) {
    console.warn('Failed to hydrate weight calculator entries', error);
    return [];
  }
};

const formatWeight = (grams, formatter) => {
  if (!Number.isFinite(grams) || grams <= 0) return formatter('weightCalculator.zeroWeight');
  if (grams >= 1000) {
    const kg = (grams / 1000).toFixed(2);
    return formatter('weightCalculator.weightKg', { kg, grams: Math.round(grams) });
  }
  return formatter('weightCalculator.weightGrams', { grams: Math.round(grams) });
};

function WeightCalculatorPopup({ isOpen, onClose, gearItems = [] }) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState(() => loadInitialEntries());
  const [selectedGearId, setSelectedGearId] = useState('');
  const [customName, setCustomName] = useState('');
  const [customWeight, setCustomWeight] = useState('');
  const [banner, setBanner] = useState(null);

  const persistEntries = useCallback((nextOrUpdater) => {
    setEntries((prev) => {
      const nextEntries = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextEntries));
        } catch (error) {
          console.warn('Failed to persist weight calculator state', error);
        }
      }
      return nextEntries;
    });
  }, []);

  const catalogOptions = useMemo(() => {
    return gearItems
      .map((item) => {
        const id = String(item?.id ?? item?._id ?? '');
        
        const genderLower = String(item?.gender || '').toLowerCase();
        const nameLower = String(item?.name || '').toLowerCase();
        const isWomensProduct = genderLower.includes('women') || genderLower.includes('female') || genderLower === 'w' || genderLower.includes('donna') || genderLower.includes('femme') || nameLower.includes("women's") || nameLower.includes(" womens") || nameLower.endsWith(" w");

        let label = `${item?.brand ? `${item.brand} ` : ''}${item?.name || 'Unnamed gear'}`.trim();
        if (isWomensProduct) {
          label += ' ♀';
        }

        const weight = Number.parseFloat(item?.weight);
        if (!id || !label || !Number.isFinite(weight) || weight <= 0) {
          return null;
        }
        return { 
          id, 
          label, 
          weight, 
          category: item?.category || 'Uncategorized' 
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [gearItems]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const categories = useMemo(() => {
    return Array.from(new Set(catalogOptions.map((o) => o.category))).sort();
  }, [catalogOptions]);

  const filteredCatalogOptions = useMemo(() => {
    return catalogOptions.filter((item) => {
      const matchSearch = item.label.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCategory = selectedCategory ? item.category === selectedCategory : true;
      return matchSearch && matchCategory;
    });
  }, [catalogOptions, searchTerm, selectedCategory]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const totalWeight = useMemo(() => {
    return entries.reduce((sum, entry) => sum + (Number(entry.weight) || 0), 0);
  }, [entries]);

  const showBanner = (text, tone = 'info') => {
    if (!text) {
      setBanner(null);
      return;
    }
    setBanner({ text, tone });
  };

  const handleAddFromCatalog = () => {
    if (!selectedGearId) {
      showBanner(t('weightCalculator.selectGearPrompt'), 'error');
      return;
    }
    const gear = catalogOptions.find((item) => item.id === selectedGearId);
    if (!gear) {
      showBanner(t('weightCalculator.gearUnavailable'), 'error');
      return;
    }
    const newEntry = {
      id: `gear-${gear.id}-${Date.now()}`,
      label: gear.label,
      weight: gear.weight,
      source: 'catalog',
      gearId: gear.id,
    };
    persistEntries((prev) => [...prev, newEntry]);
    setSelectedGearId('');
    showBanner(t('weightCalculator.catalogAdded', { label: gear.label }), 'success');
  };

  const handleAddCustom = (event) => {
    event.preventDefault();
    const label = customName.trim();
    const weight = Number.parseFloat(customWeight);
    if (!label) {
      showBanner(t('weightCalculator.customMissingName'), 'error');
      return;
    }
    if (!Number.isFinite(weight) || weight <= 0) {
      showBanner(t('weightCalculator.customInvalidWeight'), 'error');
      return;
    }
    const newEntry = {
      id: `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      label,
      weight,
      source: 'custom',
      gearId: null,
    };
    persistEntries((prev) => [...prev, newEntry]);
    setCustomName('');
    setCustomWeight('');
    showBanner(t('weightCalculator.customAdded', { label }), 'success');
  };

  const handleRemoveEntry = (entryId) => {
    persistEntries((prev) => prev.filter((entry) => entry.id !== entryId));
  };

  const handleReset = () => {
    if (!entries.length) return;
    const confirmReset = window.confirm(t('weightCalculator.resetConfirm'));
    if (!confirmReset) return;
    persistEntries([]);
    showBanner(t('weightCalculator.resetSuccess'), 'info');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('weightCalculator.ariaLabel')}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div className={styles.modal}>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('weightCalculator.closeAria')}>
          ×
        </button>
        <h2 className={styles.title}>{t('weightCalculator.title')}</h2>
        <p className={styles.subtitle}>{t('weightCalculator.subtitle')}</p>

        {banner && (
          <div className={`${styles.banner} ${banner.tone === 'error' ? styles.bannerError : banner.tone === 'success' ? styles.bannerSuccess : styles.bannerInfo}`}>
            {banner.text}
          </div>
        )}

        <div className={styles.formGrid}>
          <div className={styles.card}>
            <h3>{t('weightCalculator.catalogTitle')}</h3>
            {catalogOptions.length === 0 ? (
              <p className={styles.hint}>{t('weightCalculator.catalogEmpty')}</p>
            ) : (
              <>
                <label className={styles.label} htmlFor="catalogSearch">{t('weightCalculator.searchItem', 'Search item')}</label>
                <input
                  id="catalogSearch"
                  type="text"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setSelectedGearId('');
                  }}
                  placeholder={t('weightCalculator.searchItemPlaceholder', 'Search by name...')}
                />

                <label className={styles.label} htmlFor="catalogCategory">{t('weightCalculator.selectCategory', 'Filter by Category')}</label>
                <select
                  id="catalogCategory"
                  value={selectedCategory}
                  onChange={(event) => {
                    setSelectedCategory(event.target.value);
                    setSelectedGearId('');
                  }}
                >
                  <option value="">{t('weightCalculator.allCategories', 'All Categories')}</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>

                <label className={styles.label} htmlFor="catalogSelect">{t('weightCalculator.pickItem')}</label>
                <div className={styles.selectRow}>
                  <select
                    id="catalogSelect"
                    value={selectedGearId}
                    onChange={(event) => setSelectedGearId(event.target.value)}
                  >
                    <option value="">{t('weightCalculator.selectPlaceholder')}</option>
                    {filteredCatalogOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label} · {formatWeight(item.weight, t)}
                      </option>
                    ))}
                  </select>
                  <button type="button" className={styles.primaryBtn} onClick={handleAddFromCatalog} disabled={!selectedGearId}>
                    {t('weightCalculator.addButton')}
                  </button>
                </div>
                <p className={styles.hint}>{filteredCatalogOptions.length} items found</p>
              </>
            )}
          </div>

          <form className={styles.card} onSubmit={handleAddCustom}>
            <h3>{t('weightCalculator.customTitle')}</h3>
            <label className={styles.label} htmlFor="customName">{t('weightCalculator.customName')}</label>
            <input
              id="customName"
              type="text"
              value={customName}
              onChange={(event) => setCustomName(event.target.value)}
              placeholder={t('weightCalculator.customNamePlaceholder')}
            />
            <label className={styles.label} htmlFor="customWeight">{t('weightCalculator.customWeight')}</label>
            <input
              id="customWeight"
              type="number"
              min="1"
              step="1"
              value={customWeight}
              onChange={(event) => setCustomWeight(event.target.value)}
              placeholder={t('weightCalculator.customWeightPlaceholder')}
            />
            <button type="submit" className={styles.secondaryBtn}>{t('weightCalculator.customAddButton')}</button>
          </form>
        </div>

        <div className={styles.entriesSection}>
          <div className={styles.entriesHeader}>
            <h3>{t('weightCalculator.itemsTitle', { count: entries.length })}</h3>
            <button type="button" className={styles.resetBtn} onClick={handleReset} disabled={!entries.length}>
              {t('weightCalculator.resetButton')}
            </button>
          </div>
          {entries.length === 0 ? (
            <p className={styles.hint}>{t('weightCalculator.emptyHint')}</p>
          ) : (
            <ul className={styles.entryList}>
              {entries.map((entry) => (
                <li key={entry.id} className={styles.entryItem}>
                  <div>
                    <p className={styles.entryLabel}>{entry.label}</p>
                    <p className={styles.entryMeta}>
                      {entry.source === 'catalog' ? t('weightCalculator.catalogTag') : t('weightCalculator.customTag')} · {formatWeight(entry.weight, t)}
                    </p>
                  </div>
                  <button type="button" className={styles.removeBtn} onClick={() => handleRemoveEntry(entry.id)}>
                    {t('weightCalculator.removeButton')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.summaryRow}>
          <div>
            <p className={styles.summaryLabel}>{t('weightCalculator.totalLabel')}</p>
            <p className={styles.summaryValue}>{formatWeight(totalWeight, t)}</p>
          </div>
          <button type="button" className={styles.closeSecondary} onClick={onClose}>
            {t('action.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WeightCalculatorPopup;
